import React, { useState, useEffect } from 'react';
import {Link, useNavigate, useParams} from "react-router-dom";
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation';
import GameContainer from './GameContainer';
import * as utils from '../common/Utils.js'
import { useLeagueData } from '../contexts/LeagueDataContext.js';
import { useConfig } from '../contexts/ConfigContext.js';

const Team = () => {
  const navigate = useNavigate();
  const { teamID, seasonID } = useParams();
  const { teams: allTeams, seasonTeams: allSeasonTeams, players: allPlayers, games: allGames, dataLoaded } = useLeagueData(['teams', 'seasonTeams', 'players', 'games']);
  const config = useConfig();
  const season = seasonID || config.currentSeason;

  const [games, setGames] = useState([]);
  const [players, setPlayers] = useState([])
  const [teams, setTeams] = useState([])
  const [seasonTeams, setSeasonTeams] = useState([])
  const [dataReady, setDataReady] = useState(false);

  useEffect(() => {
    if (!dataLoaded) return;

    setPlayers(allPlayers);
    setGames(allGames);

    const thisSeasonTeams = allSeasonTeams.filter(st => st.season == season);
    const mergedTeams = allTeams.map(t => {
      const seasonTeam = thisSeasonTeams.find(st => t.id === st.teamID);

      return { ...t, ...seasonTeam };
    });
    setTeams(mergedTeams);
    setSeasonTeams(mergedTeams);
    setDataReady(true);
  }, [dataLoaded, allTeams, allSeasonTeams, allPlayers, allGames]);

  function displayTeamName () {
    if (teams.length > 0) {
      return 'St. ' + utils.getTeamForID(teams, teamID).name
    } else {
      return ''
    }
  }

  function displayPlayer (player) {
    let captainClass = '';
    let team = utils.getTeamForID(teams, teamID)

    if (player.id == team.captain) {
      captainClass = 'captain'
    }
    return (
      <tr key={player.id + "-team-row"}>
        <td className={ `league-table-data-name user-name ${captainClass}` }><a className="unstyled-link" href={"/players/" + player.id }>{ utils.displayJerseyNumber(player) } { utils.displayPlayerName(player) }</a></td>
        <td className="league-table-data wide"><span>{ utils.displayPlayerHeight(player) || '-' }</span></td>
      </tr>
    );
  }

  function jerseyNumberSort (a, b) {
    if (a.jerseyNumber == '' && b.jerseyNumber == '') {
      return 0;
    } else if (a.jerseyNumber == '') {
      return 1;
    } else if (b.jerseyNumber == '') {
      return -1;
    } else {
      let number1 = parseInt(a.jerseyNumber)
      let number2 = parseInt(b.jerseyNumber)

      return number1 < number2 ? -1 : 1
    }
  }

  function displayPlayers () {
    if (players.length == 0){
      return;
    }

    const tm = utils.getTeamForID(teams, teamID)

    if (!tm || !tm.players) {
      return;
    }

    const teamPlayers = tm.players.map(id => utils.getPlayerByID(players, id)).filter(player => player);

    let sortedPlayers = teamPlayers.sort((a, b) => jerseyNumberSort(a, b))
    let playerElements = sortedPlayers.map((player, place) => displayPlayer(player))
    return playerElements;
  }

  function displayLastGame () {
    let game = getLastGame();

    if (!game) {
      return;
    }

    return (
      <>
        <p className="league-game-label-small text-align-center">Last Game</p>
        { displayFinishedGameThisWeek(game) }
      </>
    )
  }

  function displayNextGame () {
    let game = getNextGame();

    if (!game) {
      return;
    }

    return (
      <>
        <p className="league-game-label-small text-align-center">Next Game</p>
        { displayGameThisWeek(game) }
      </>
    )
  }

  function getLastGame () {
    let allCompletedGames = games.filter((game) => game.status === 'completed' && game.season == season && (game.awayTeamID === teamID || game.homeTeamID === teamID))
    return allCompletedGames[allCompletedGames.length - 1];
  }

  function getNextGame () {
    let allScheduledGames = games.filter((game) => game.status === 'scheduled' && game.season == season && (game.awayTeamID === teamID || game.homeTeamID === teamID))
    return allScheduledGames[0];
  }

  function abbvWeekday (idx) {
    let weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return weekday[idx];
  }

  function displayGameThisWeek (game) {
    let awayTeam;
    let homeTeam;

    if (game.awayTeamID) {
      awayTeam = utils.getTeamForID(teams, game.awayTeamID);
    } else {
      const placeholderName = game.awayTeamPlaceholder
      awayTeam = {
        seed: '',
        name: placeholderName || 'TBD'
      };
    }

    if (game.homeTeamID) {
      homeTeam = utils.getTeamForID(teams, game.homeTeamID);
    } else {
      const placeholderName = game.homeTeamPlaceholder
      homeTeam = {
        seed: '',
        name: placeholderName || 'TBD'
      };
    }


    let hours = game.date.getHours();
    let hours_label = 'AM'
    if(hours >= 12){
      hours_label = 'PM';
    }

    if(hours > 12){
      hours -= 12;
    }
    if(hours == 0){
      hours = 12;
    }

    if (game.type == 'playoff') {
      return (
        <GameContainer games={games} teams={teams} gameID={game.id}></GameContainer>
      )
    } else {
      return (
        <div className='league-game-container stretch-game-width' onClick={() => {
          navigate(`/games/${game.id}`)
        }} key={game.id + '-game-container'}>
          <div className='league-game-team-names-and-scores'>
            <div className='league-game-team-names'>
              <p className={ game.winner === game.awayTeamID ? 'league-game-team-name game-winner' : 'league-game-team-name' } key={game.id + '-away-name'}>{ awayTeam.name.toUpperCase() }</p>
              <p className={ game.winner === game.homeTeamID ? 'league-game-team-name game-winner' : 'league-game-team-name' } key={game.id + '-home-name'}>{ homeTeam.name.toUpperCase() }</p>
            </div>
            <div className='league-game-team-records'>
              <p className='league-game-team-record' key={game.id + '-away-record'}>{ utils.getRecordForID(games, game.awayTeamID, season) }</p>
              <p className='league-game-team-record' key={game.id + '-home-record'}>{ utils.getRecordForID(games, game.homeTeamID, season) }</p>
            </div>
          </div>
          <div className='league-game-time-and-stats'>
            <p className='league-game-time'>{ abbvWeekday(game.date.getDay()) }, { game.date.getMonth() + 1 }/{ game.date.getDate() }</p>
            <p className='league-game-time'>{ hours }:{ game.date.getMinutes() } { hours_label }</p>
            <p className='league-game-time'>{ utils.getLocationName(game.location, true) }</p>
          </div>
        </div>
      )
    }
  }

  function displayFinishedGameThisWeek (game) {
    let awayTeam = utils.getTeamForID(teams, game.awayTeamID);
    let homeTeam = utils.getTeamForID(teams, game.homeTeamID);

    let hours = game.date.getHours();
    let hours_label = 'AM'
    if(hours >= 12){
      hours_label = 'PM';
    }

    if(hours > 12){
      hours -= 12;
    }
    if(hours == 0){
      hours = 12;
    }
    return (
      <div className='league-game-container stretch-game-width' onClick={() => {
        navigate(`/games/${game.id}`)
      }} key={game.id + '-game-container'}>
        <div className='league-game-team-names-and-scores'>
          <div className='league-game-team-names'>
            <p className={ game.winner === game.awayTeamID ? 'league-game-team-name game-winner' : 'league-game-team-name' } key={game.id + '-away'}>{ awayTeam.name.toUpperCase() }</p>
            <p className={ game.winner === game.homeTeamID ? 'league-game-team-name game-winner' : 'league-game-team-name' } key={game.id + '-home'}>{ homeTeam.name.toUpperCase() }</p>
          </div>
          <div className='league-game-team-scores'>
            <p className={ game.winner === game.awayTeamID ? 'league-game-team-score game-winner' : 'league-game-team-score' } key={game.id + '-away-score'}>{ game.status === 'completed' ? game.score.away : '' }</p>
            <p className={ game.winner === game.homeTeamID ? 'league-game-team-score game-winner' : 'league-game-team-score' } key={game.id + '-home-score'}>{ game.status === 'completed' ? game.score.home : '' }</p>
          </div>
        </div>
        <div className='league-game-time-and-stats'>
          <p className='league-game-time'><b>{ game.status === 'completed' ? 'Final' : 'Pending' }</b></p>
          <p className='league-game-time'>{ game.date.getMonth() + 1 }/{ game.date.getDate() }</p>
        </div>
      </div>
    )
  }

  let this_team = utils.getTeamForID(teams, teamID)

  if (dataReady == false) {
    return (
      <div className="Homepage">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="">
        <div className="league-content">
          <div className="league-header">
            <div className="league-header-top">
              <p id="teamName" className="league-title-standard-secondary">{ this_team.name } <span style={{color: '#2B4593'}}>{ seasonID ? `(Season ${seasonID})` : '' }</span></p>
              <p id="teamName" className="league-title-standard-secondary">{ utils.displayTeamConference(this_team) }</p>
              <a className="league-title-standard-small league-title-link text-align-center" href={ '/who-do-you-play-for/' + teamID}>Who Do You Play For?</a>
            </div>
          </div>

          <div className="league-body">
            <p className="league-title-standard-small">({ utils.getRecordForID(games, teamID, season) })</p>
            <a className="league-type league-mini-header-link" href={`/schedule/teams/${teamID}`}>All Team Games</a>
            <div className="league-table-container team-page">
              { displayLastGame() }
              { displayNextGame() }
            </div>
          </div>

          <div className="league-body">
            <p className="league-title-standard-secondary text-align-center">Roster</p>
            <div className="league-table-container">
              <table className="table league-table">
              <thead className="league-table-heading-hole">
                  <tr>
                    <th className="league-table-heading-name"><span>PLAYER</span></th>
                    <th className="league-table-heading hole"><span>HEIGHT</span></th>
                  </tr>
                </thead>
                <thead className="league-table-heading-total">
                  <tr>
                    <th className="league-table-heading-name"><span>PLAYER</span></th>
                    <th className="league-table-heading-small"><span>HEIGHT</span></th>
                  </tr>
                </thead>
                <tbody>
                  { displayPlayers() }
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Footer/>
      </div>
    );
  }
}

export default Team;
